import {LocalDate} from '@js-joda/core';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import IconButton from '@material-ui/core/IconButton';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Tooltip from '@material-ui/core/Tooltip';
import AddIcon from '@material-ui/icons/Add';
import {Autocomplete, makeValidate} from 'mui-rff';
import React, {useCallback, useMemo, useState} from 'react';
import {Form} from 'react-final-form';
import * as yup from 'yup';
import LoadingButton from '../../LoadingIndicator/LoadingButton';
import {ClientServiceTeam, Employee, Project, ProjectSchedule, UNKNOWN_TEAM} from '../Utils/EmployeeSchedulesCompiler';
import {useScheduleContext} from '../Utils/ScheduleContextProvider';

const useStyles = makeStyles(theme => ({
    cancel: {
        color: theme.palette.grey['500'],
    },
}));

type Props = {
    employee : Employee;
    schedules : ProjectSchedule[];
    startDate : LocalDate;
    teams : ClientServiceTeam[];
};

type FormData = {
    project : Project;
};

const AddProjectButton : React.FC<Props> = ({employee, schedules, startDate, teams} : Props) => {
    const classes = useStyles();
    const scheduleContext = useScheduleContext();
    const [open, setOpen] = useState(false);

    const [validate, projects] = useMemo(() => {
        const projects = scheduleContext.activeProjects.filter(
            project => !schedules.some(projectSchedule => projectSchedule.project.id === project.id)
        );
        const schema = yup.object({
            project: yup.mixed<Project>().label('Project').required(),
        }).required();
        const validate = makeValidate(schema);

        return [validate, projects];
    }, [scheduleContext.activeProjects, schedules]);

    const handleSubmit = useCallback(async (formData : FormData) => {
        await scheduleContext.addProject(employee.id, formData.project, startDate);
        setOpen(false);
    }, [scheduleContext, employee, startDate]);

    return (
        <>
            <Tooltip title="Add project">
                <IconButton size="small" color="inherit" onClick={() => setOpen(true)}>
                    <AddIcon/>
                </IconButton>
            </Tooltip>

            <Form
                subscription={{submitting: true, pristine: true}}
                onSubmit={handleSubmit}
                validate={validate}
                render={({handleSubmit, pristine, submitting, form: {reset}}) => (
                    <Dialog
                        open={open}
                        onClose={(_event, reason) => {
                            if(reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
                                setOpen(false)
                            }
                        }}
                        disableEscapeKeyDown={submitting}
                        maxWidth="sm"
                        fullWidth
                        PaperProps={{
                            component: 'form',
                            onSubmit: handleSubmit,
                            // @ts-ignore Incomplete prop definition
                            noValidate: true,
                        }}
                        TransitionProps={{
                            onEntered: () => {
                                reset()
                            },
                        }}
                    >
                        <DialogTitle>Add Project</DialogTitle>
                        <DialogContent>
                            <Autocomplete
                                name="project"
                                label="Project"
                                options={projects}
                                filterOptions={(options, state) => {
                                    const words = state.inputValue.toLocaleLowerCase().split(' ');

                                    return options.filter(option => {
                                        const contents = [
                                            teams.find((team) => team.id === option.teamId)?.abbreviation?.toLocaleLowerCase(),
                                            option.name.toLocaleLowerCase(),
                                        ].join(' - ');

                                        for (const word of words) {
                                            if (!contents.includes(word)) {
                                                return false;
                                            }
                                        }

                                        return true;
                                    }).slice(0, 100);
                                }}
                                getOptionLabel={option => {
                                    if (!option) {
                                        return '';
                                    }
                                    const team = teams.find((team) => team.id === option.teamId) ?? UNKNOWN_TEAM;
                                    return `${team.abbreviation} - ${option.name}`;
                                }}
                                textFieldProps={{variant: 'outlined', autoFocus: true}}
                                disableClearable
                                helperText="Search is limited to 100 results"
                            />
                        </DialogContent>
                        <DialogActions>
                            <Button
                                onClick={() => setOpen(false)}
                                disabled={submitting}
                                className={classes.cancel}
                            >
                                Cancel
                            </Button>
                            <LoadingButton
                                type="submit"
                                color="primary"
                                loading={submitting}
                                disabled={pristine || submitting}
                            >
                                Add
                            </LoadingButton>
                        </DialogActions>
                    </Dialog>
                )}
            />
        </>
    );
};

export default AddProjectButton;
